/**
 * Mensaje inicial de una propiedad («Información para el huésped») y el flujo
 * «Copiar instrucciones» del calendario.
 *
 * Es la ÚNICA implementación: el botón del modal de detalle de la reserva y el
 * ítem «Copiar instrucciones» del menú contextual de la grilla pasan por
 * `copiarInstruccionesDePropiedad`, así que leen el mismo dato y avisan igual.
 *
 * Por qué existe (round 2026-10-28): el calendario armaba su lista de
 * propiedades con un mapper que descartaba `mensaje_inicial`, y los dos
 * puntos de copia leían `prop.mensajeInicial ?? prop.mensaje_inicial` de un
 * objeto que nunca lo tenía → siempre avisaban «Esta propiedad no tiene mensaje
 * inicial configurado», aunque estuviera escrito. Ver docs/modulo-calendario.md.
 */

import { propiedadesApi } from "../services/propiedades.api";
import { getMsg } from "./antdStatic";

/** Forma mínima que entiende el helper: el modelo (snake_case) o el `Property` del calendario (camelCase). */
export interface PropiedadConMensaje {
  mensajeInicial?: string | null;
  mensaje_inicial?: string | null;
}

/**
 * Texto EXACTO del mensaje inicial, o "" si no hay nada que copiar. Un mensaje
 * de solo espacios/saltos de línea cuenta como vacío (no hay nada que pegarle
 * al huésped), pero uno con contenido se devuelve sin tocar (sin `trim`).
 */
export function obtenerMensajeInicial(propiedad: PropiedadConMensaje | null | undefined): string {
  const crudo = propiedad?.mensajeInicial ?? propiedad?.mensaje_inicial;
  if (typeof crudo !== "string") return "";
  return crudo.trim() === "" ? "" : crudo;
}

export type ResultadoCopiaInstrucciones =
  /** Quedó en el portapapeles. */
  | "copiado"
  /** La propiedad se leyó bien y de verdad no tiene mensaje inicial. */
  | "sin_mensaje"
  /** No se pudo leer la propiedad (no está en la lista y la consulta falló). */
  | "no_cargada"
  /** Se tenía el texto pero el navegador no dejó escribir el portapapeles. */
  | "error_portapapeles";

export const AVISOS_INSTRUCCIONES = {
  copiado: "Instrucciones copiadas al portapapeles",
  sin_mensaje:
    "Esta propiedad no tiene mensaje inicial. Configúralo en Propiedades → Información para el huésped.",
  no_cargada: "No se pudo cargar la propiedad para leer su mensaje inicial. Intenta de nuevo.",
  error_portapapeles: "No se pudo copiar al portapapeles. Revisa los permisos del navegador.",
} as const;

/** ¿Este navegador acepta un `ClipboardItem` con contenido diferido (promesa)? Safari, Chrome y Firefox recientes. */
function puedeEscribirConPromesa(): boolean {
  return (
    typeof ClipboardItem !== "undefined" &&
    typeof navigator !== "undefined" &&
    typeof navigator.clipboard?.write === "function"
  );
}

/**
 * Escribe el mensaje fresco de `propiedadId` en el portapapeles llamando a `clipboard.write` AHORA (dentro del
 * gesto del clic) con un texto que todavía se está pidiendo a la red. Si la propiedad no carga o no tiene
 * mensaje, la promesa se rechaza y el portapapeles queda intacto (no se pisa con nada).
 */
async function copiarConPromesa(
  propiedadId: number,
): Promise<"copiado" | "sin_mensaje" | "no_cargada" | "error_portapapeles"> {
  const estado = { motivo: null as "sin_mensaje" | "no_cargada" | null };
  const texto = (async (): Promise<Blob> => {
    const fresca = await propiedadesApi.getById(propiedadId);
    const data = fresca?.data as PropiedadConMensaje | undefined;
    if (!data) {
      estado.motivo = "no_cargada";
      throw new Error("propiedad no cargada");
    }
    const mensaje = obtenerMensajeInicial(data);
    if (!mensaje) {
      estado.motivo = "sin_mensaje";
      throw new Error("propiedad sin mensaje inicial");
    }
    return new Blob([mensaje], { type: "text/plain" });
  })();
  // Si `write` falla antes de consumir la promesa, su rechazo posterior no debe quedar sin atender.
  texto.catch(() => undefined);
  try {
    await navigator.clipboard.write([new ClipboardItem({ "text/plain": texto })]);
    return "copiado";
  } catch {
    // Un rechazo del texto trae su `motivo`; si no hay motivo, fue el navegador el que no dejó escribir.
    return estado.motivo ?? "error_portapapeles";
  }
}

async function escribirPortapapeles(texto: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    return false;
  }
}

/**
 * Copia al portapapeles el mensaje inicial de `propiedadId` y avisa al usuario
 * (éxito o el motivo exacto del fallo). Devuelve el resultado para que el
 * llamador pueda además reflejarlo en su UI (p. ej. el «¡Copiado!» del botón).
 *
 * Orden: primero el listado que ya tiene el calendario (síncrono, conserva el
 * gesto del usuario para el portapapeles); solo si ahí no hay texto se consulta
 * la propiedad fresca, que cubre un listado desactualizado (el mensaje se
 * editó en /propiedades con el calendario ya abierto) y distingue «no tiene»
 * de «no se pudo cargar».
 *
 * Esa consulta es una ESPERA de red, y el portapapeles se escribía DESPUÉS de ella, fuera del gesto del
 * usuario (el clic). Safari/iOS exige que `clipboard.writeText` ocurra dentro del gesto y lo rechaza con
 * `NotAllowedError` si hubo una espera de red en medio: el aviso decía «No se pudo copiar al
 * portapapeles» justo cuando el listado estaba desactualizado (round 2026-10-28). Por eso, en ese camino,
 * se llama a `clipboard.write` DENTRO del gesto con un `ClipboardItem` cuyo contenido es una PROMESA: el
 * navegador reserva el permiso y espera el texto. Sin `ClipboardItem` (Firefox antiguo) queda el
 * `writeText` de siempre.
 */
export async function copiarInstruccionesDePropiedad(
  propiedadId: number | null | undefined,
  propiedades: ReadonlyArray<PropiedadConMensaje & { id: number }>,
): Promise<ResultadoCopiaInstrucciones> {
  const avisar = (r: ResultadoCopiaInstrucciones) => {
    const m = getMsg();
    if (r === "copiado") m?.success(AVISOS_INSTRUCCIONES.copiado);
    else if (r === "sin_mensaje") m?.warning(AVISOS_INSTRUCCIONES.sin_mensaje);
    else m?.error(AVISOS_INSTRUCCIONES[r]);
    return r;
  };

  const enLista = propiedades.find((p) => p.id === propiedadId);
  let mensaje = obtenerMensajeInicial(enLista);

  if (!mensaje) {
    if (propiedadId == null) return avisar("no_cargada");
    if (puedeEscribirConPromesa()) return avisar(await copiarConPromesa(propiedadId));
    const fresca = await propiedadesApi.getById(propiedadId);
    const data = fresca?.data as PropiedadConMensaje | undefined;
    if (!data) return avisar("no_cargada");
    mensaje = obtenerMensajeInicial(data);
    if (!mensaje) return avisar("sin_mensaje");
  }

  return avisar((await escribirPortapapeles(mensaje)) ? "copiado" : "error_portapapeles");
}
